import { useState, useEffect } from 'react';
import { api } from '../api';
import { useData } from '../hooks/useData';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const STATUS_COLOR = { done: 'var(--accent)', error: 'var(--red)', transcribing: 'var(--blue)', analysing: 'var(--yellow)', pending: 'var(--text-3)' };

function StatBox({ value, label, color, sub }) {
  return (
    <div style={{ background: 'var(--bg-3)', borderRadius: 10, padding: '1rem 1.25rem', border: '1px solid var(--border)' }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, color, lineHeight: 1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: sub ? 2 : 0 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>{sub}</div>}
    </div>
  );
}

function QuickAction({ icon, title, desc, onClick, accent }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '1rem',
      padding: '1rem 1.25rem',
      background: accent ? 'var(--accent-dark)' : 'var(--bg-3)',
      border: `1px solid ${accent ? 'rgba(0,255,136,0.25)' : 'var(--border)'}`,
      borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = accent ? 'var(--accent)' : 'var(--border-2)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = accent ? 'rgba(0,255,136,0.25)' : 'var(--border)'}
    >
      <div style={{ fontSize: 22, width: 40, height: 40, background: 'var(--bg-2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: accent ? 'var(--accent)' : 'var(--text)', marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{desc}</div>
      </div>
      <span style={{ color: 'var(--text-3)', fontSize: 16 }}>→</span>
    </div>
  );
}

export default function HomePage({ onNavigate, onNewMeeting, stats, refreshTrigger }) {
  const [meetings, setMeetings] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  const { meetings: allMeetings, actions: allActions, loading: dataLoading } = useData(refreshTrigger);
  useEffect(() => {
    setMeetings(allMeetings);
    setActions(allActions);
    setLoading(dataLoading);
  }, [allMeetings, allActions, dataLoading]);

  const markDone = async (meetingId, itemId) => {
    await api.updateActionItem(meetingId, itemId, { status: 'done' });
    setActions(prev => prev.map(a => a.id === itemId ? { ...a, status: 'done' } : a));
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const doneMeetings = meetings.filter(m => m.status === 'done');
  const openActions = actions.filter(a => a.status !== 'done');
  const overdueActions = openActions.filter(a => {
    if (!a.deadline) return false;
    const d = new Date(a.deadline);
    return !isNaN(d) && d < new Date();
  });
  const dueSoon = openActions.filter(a => {
    if (!a.deadline) return false;
    const d = new Date(a.deadline);
    if (isNaN(d)) return false;
    const diff = (d - Date.now()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 1;
  });

  const completionRate = actions.length === 0 ? 0 : Math.round((actions.filter(a => a.status === 'done').length / actions.length) * 100);

  if (loading) return (
    <div className="page fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {[120, 80, 200, 160].map((h, i) => <div key={i} className="skeleton" style={{ height: h, borderRadius: 12 }} />)}
    </div>
  );

  return (
    <div className="page fade-in">

      {/* ── Top bar ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.75rem' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>{greeting} 👋</h1>
          <p style={{ fontSize: 13, color: 'var(--text-2)' }}>
            {doneMeetings.length === 0
              ? 'Ready to analyse your first meeting'
              : `${doneMeetings.length} meeting${doneMeetings.length > 1 ? 's' : ''} analysed · ${openActions.length} open task${openActions.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => onNewMeeting('file')}>+ New Meeting</button>
      </div>

      {/* ── Stats row ── */}
      <div className="grid-stats" style={{ marginBottom: '1.5rem' }}>
        <StatBox value={doneMeetings.length} label="Meetings" color="var(--accent)" sub="analysed" />
        <StatBox value={openActions.length} label="Open Tasks" color={openActions.length > 10 ? 'var(--red)' : 'var(--yellow)'} sub={overdueActions.length > 0 ? `${overdueActions.length} overdue` : 'all on track'} />
        <StatBox value={overdueActions.length} label="Overdue" color={overdueActions.length > 0 ? 'var(--red)' : 'var(--text-3)'} sub={overdueActions.length > 0 ? 'needs attention' : 'none overdue'} />
        <StatBox value={`${completionRate}%`} label="Task Completion" color={completionRate >= 80 ? 'var(--accent)' : completionRate >= 50 ? 'var(--yellow)' : completionRate === 0 ? 'var(--text-3)' : 'var(--red)'} sub={actions.length === 0 ? 'no tasks yet' : `${actions.filter(a => a.status === 'done').length}/${actions.length} done`} />
      </div>

      {/* ── Main grid ── */}
      <div className="grid-main">

        {/* Quick actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Quick Actions</div>
          <QuickAction icon="🎙️" title="Upload Meeting Recording" desc="MP3, MP4, WAV — transcribed automatically" onClick={() => onNewMeeting('file')} accent />
          <QuickAction icon="✎" title="Paste a Transcript" desc="From Zoom, Google Meet, Otter.ai" onClick={() => onNewMeeting('transcript')} />
          <QuickAction icon="⬡" title="Import from Zoom" desc="Pull transcripts from cloud recordings" onClick={() => onNavigate('zoom')} />
          <QuickAction icon="▦" title="View Analytics" desc="Meeting trends, owner leaderboard" onClick={() => onNavigate('analytics')} />
        </div>

        {/* Due soon */}
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>
              ⏰ Due Soon {dueSoon.length > 0 && <span style={{ background: 'var(--yellow)', color: '#000', borderRadius: 4, padding: '1px 6px', marginLeft: 4 }}>{dueSoon.length}</span>}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('actions')} style={{ fontSize: 11 }}>All tasks →</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0.625rem' }}>
            {overdueActions.length > 0 && (
              <div style={{ padding: '0.5rem 0.75rem', marginBottom: '0.5rem', background: 'var(--red-dim)', border: '1px solid rgba(255,68,85,0.2)', borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--red)', fontFamily: 'var(--mono)', marginBottom: 4 }}>⚠ OVERDUE</div>
                {overdueActions.slice(0, 2).map(a => (
                  <div key={a.id} style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 2 }}>• {a.description}</div>
                ))}
                {overdueActions.length > 2 && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>+{overdueActions.length - 2} more</div>}
              </div>
            )}
            {dueSoon.length === 0 && overdueActions.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
                Nothing due today
              </div>
            ) : dueSoon.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.5rem 0.625rem', borderRadius: 6, marginBottom: 4, background: 'var(--bg-3)', border: '1px solid var(--border)' }}>
                <button className="btn btn-ghost btn-sm" style={{ padding: '1px 5px', fontSize: 12, flexShrink: 0, color: 'var(--text-3)' }} onClick={() => markDone(a.meeting_id, a.id)}>○</button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.description}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{a.owner ?? 'Unassigned'}</div>
                </div>
                <span className={`badge badge-${a.priority}`}>{a.priority}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Recent meetings ── */}
      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>◈ Recent Meetings</span>
          <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('meetings')} style={{ fontSize: 11 }}>View all →</button>
        </div>
        {meetings.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: '0.75rem' }}>📭</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>No meetings yet</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: '1.25rem' }}>Upload a recording or paste a transcript to get started</div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button className="btn btn-primary btn-sm" onClick={() => onNewMeeting('file')}>↑ Upload Recording</button>
              <button className="btn btn-outline btn-sm" onClick={() => onNewMeeting('transcript')}>✎ Paste Transcript</button>
            </div>
          </div>
        ) : (
          <div className="grid-meetings">
            {meetings.slice(0, 6).map((m, i) => (
              <div key={m.id} onClick={() => onNavigate('meeting-detail', m.id)} style={{
                padding: '1rem 1.25rem',
                borderRight: (i + 1) % 3 !== 0 ? '1px solid var(--border)' : 'none',
                borderBottom: i < Math.min(meetings.length, 6) - 3 ? '1px solid var(--border)' : 'none',
                cursor: 'pointer', transition: 'background 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-3)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 6 }}>
                  <span style={{ color: STATUS_COLOR[m.status], fontSize: 8 }}>●</span>
                  <span className={`badge badge-${m.status}`} style={{ fontSize: 9 }}>{m.status}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
                  {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                {m.summary && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5 }}>{m.summary}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
